import { NavLink, useParams } from "react-router-dom";
import { BarChart3, CalendarDays, Trophy } from "lucide-react";
import Seo from "@/components/site/Seo";
import { EmptyState, PageHero } from "@/components/site/kit";
import { MatchCard, indexBy } from "@/components/site/cards";
import useFetch from "@/hooks/useFetch";

const TABS = [
  { key: "", label: "All Fixtures" },
  { key: "upcoming", label: "Upcoming" },
  { key: "results", label: "Results" },
  { key: "points-table", label: "Points Table" },
  { key: "stats", label: "Statistics" },
];

const MatchList = ({ status, emptyTitle }) => {
  const { data: matches } = useFetch(`/matches${status ? `?status=${status}` : ""}`, []);
  const { data: teams } = useFetch("/teams", []);
  const { data: venues } = useFetch("/venues", []);
  if (!matches?.length) return <EmptyState testid="fixtures-empty" icon={CalendarDays} title={emptyTitle}>Match schedules, venues and timings will be published here once officially confirmed.</EmptyState>;
  return <div className="grid gap-4 md:grid-cols-2">{matches.map((m) => <MatchCard key={m.id} match={m} teams={indexBy(teams)} venues={indexBy(venues)} />)}</div>;
};

const Form = ({ form }) => (
  <div className="flex gap-1">
    {(form || "").split(/[,\s]+/).filter(Boolean).slice(-5).map((r, i) => (
      <span key={i} className={`flex h-6 w-6 items-center justify-center rounded-sm text-[10px] font-bold ${r.toUpperCase() === "W" ? "bg-[#00E5FF] text-[#0A192F]" : r.toUpperCase() === "L" ? "bg-[#FF5722] text-white" : "bg-white/15"}`}>{r.toUpperCase()}</span>
    ))}
  </div>
);

export const PointsTable = () => {
  const { data: rows } = useFetch("/standings", []);
  const { data: teams } = useFetch("/teams", []);
  const tMap = indexBy(teams);
  if (!rows?.length) return <EmptyState testid="points-empty" icon={Trophy} title="Points table will be available once the season begins" />;
  return (
    <div className="overflow-x-auto rounded-md border border-white/10">
      <table data-testid="points-table" className="w-full min-w-[760px] text-left text-sm">
        <thead className="bg-[#112240] text-[11px] uppercase tracking-widest text-slate-400">
          <tr>{["Pos", "Team", "P", "W", "L", "NR", "Pts", "NRR", "Form"].map((h) => <th key={h} className="p-4">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`points-row-${r.position}`} className="transition-colors hover:bg-white/5">
              <td className="p-4 font-score text-lg font-bold text-[#00E5FF]">{r.position}</td>
              <td className="p-4 font-semibold">{tMap[r.team_id]?.name || "TBA"}</td>
              <td className="p-4 font-score">{r.played}</td><td className="p-4 font-score">{r.won}</td>
              <td className="p-4 font-score">{r.lost}</td><td className="p-4 font-score">{r.no_result}</td>
              <td className="p-4 font-score text-lg font-bold">{r.points}</td>
              <td className="p-4 font-score">{Number(r.nrr).toFixed(3)}</td>
              <td className="p-4"><Form form={r.form} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LeaderTable = ({ title, rows, cols }) => (
  <div className="overflow-hidden rounded-md border border-white/10">
    <h3 className="bg-[#112240] p-4 font-display text-xl uppercase">{title}</h3>
    <table className="w-full text-left text-sm">
      <thead className="text-[11px] uppercase tracking-widest text-slate-400"><tr>{cols.map(([l]) => <th key={l} className="px-4 py-3">{l}</th>)}</tr></thead>
      <tbody className="divide-y divide-white/5">
        {rows.map((r) => <tr key={r.player}>{cols.map(([l, k]) => <td key={l} className="px-4 py-3 font-score">{r[k]}</td>)}</tr>)}
      </tbody>
    </table>
  </div>
);

const Stats = () => {
  const { data } = useFetch("/stats", { batting: [], bowling: [] });
  if (!data?.batting?.length && !data?.bowling?.length) return <EmptyState testid="stats-empty" icon={BarChart3} title="Statistics will be available once matches are played" />;
  return (
    <div data-testid="stats-tables" className="grid gap-6 lg:grid-cols-2">
      <LeaderTable title="Most Runs" rows={data.batting} cols={[["Player", "player"], ["Runs", "runs"], ["Balls", "balls"], ["6s", "sixes"]]} />
      <LeaderTable title="Most Wickets" rows={data.bowling} cols={[["Player", "player"], ["Wkts", "wickets"], ["Runs", "runs"], ["Overs", "overs"]]} />
    </div>
  );
};

export default function Fixtures() {
  const { tab = "" } = useParams();
  const current = TABS.find((t) => t.key === tab) || TABS[0];
  return (
    <>
      <Seo title={current.key ? current.label : "Fixtures & Results"} description="Fixtures, results, points table and statistics of the Telangana–Andhra T10 League." />
      <PageHero testid="fixtures-hero" eyebrow="Fixtures & Results" title="Match Centre" image="/images/hero.jpg">
        Fixtures will be announced soon. No matches have been scheduled yet.
      </PageHero>
      <section className="container-x py-14">
        <nav aria-label="Fixtures sections" className="mb-10 flex gap-1 overflow-x-auto rounded-md bg-[#112240] p-1">
          {TABS.map((t) => (
            <NavLink key={t.key} end to={`/fixtures${t.key ? `/${t.key}` : ""}`} data-testid={`fixtures-tab-${t.key || "all"}`}
              className={({ isActive }) => `whitespace-nowrap rounded-sm px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${isActive ? "bg-[#00E5FF] text-[#0A192F]" : "text-slate-300 hover:text-white"}`}>
              {t.label}
            </NavLink>
          ))}
        </nav>
        {current.key === "" && <MatchList emptyTitle="Fixtures will be announced soon" />}
        {current.key === "upcoming" && <MatchList status="upcoming" emptyTitle="No upcoming matches yet" />}
        {current.key === "results" && <MatchList status="completed" emptyTitle="No completed matches yet" />}
        {current.key === "points-table" && <PointsTable />}
        {current.key === "stats" && <Stats />}
      </section>
    </>
  );
}
